import React from "react";
import { Control, Controller, UseFormWatch } from "react-hook-form";
import ControllerInput from "./ControllerInput";
import TimeWindowController from "./TimeWindowController";
import {
  Shipment,
  ShipmentStopType,
} from "@api/graphql/generated/generated-types";
import { DatePicker } from "@src/common/components";
import StopDateSelector from "@src/orders/create/components/CreateStopsForm/components/StopDateSelector";
import {
  findIfStopHasFutureDateDelivery,
  findIfStopHasFutureDatePickup,
} from "@src/common/lib/shipmentStateFinders";

interface TimeRowWithTimeWindowProps {
  stopIndex: number;
  stopPathName: string;
  stopType: ShipmentStopType;
  isRecurring: boolean;
  selectedDuration: string | undefined;
  onStopDateChange: (() => void) | undefined;
  selectedShipment: Shipment | undefined;
  control: Control;
  watch: UseFormWatch<any>;
  getValues: (name: string) => any;
  formatTime: (time: number) => string | undefined;
  watchOpenTime: any;
  watchCloseTime: any;
  isSuiteRequired?: boolean;
}

// This component is used when extended time window fields are enabled
// For pickup: shows Ready + Pickup By
// For dropoff: shows Ready + Deliver By
function TimeRowWithTimeWindow({
  stopIndex,
  stopPathName,
  stopType,
  isRecurring,
  selectedDuration,
  onStopDateChange,
  selectedShipment,
  control,
  watch,
  getValues,
  formatTime,
  watchOpenTime,
  watchCloseTime,
  isSuiteRequired = false,
}: TimeRowWithTimeWindowProps) {
  const hasFutureDateDelivery =
    selectedShipment &&
    findIfStopHasFutureDateDelivery(
      getValues(`${stopPathName}[${stopIndex}]`),
      selectedShipment
    );

  const hasFutureDatePickup =
    selectedShipment &&
    findIfStopHasFutureDatePickup(
      getValues(`${stopPathName}[${stopIndex}]`),
      selectedShipment
    );

  const isPickup = stopType === ShipmentStopType.PickUp;
  const isDropoff = stopType === ShipmentStopType.DropOff;

  const shouldShowDeliverByDate =
    (isDropoff || stopType === ShipmentStopType.Return) &&
    (hasFutureDateDelivery || selectedDuration === "hasStopDates");

  const shouldShowPickupByDate =
    isPickup && (hasFutureDatePickup || selectedDuration === "hasStopDates");

  return (
    <>
      {/* Suite and Route Date row */}
      <div className="col-span-1 flex">
        <ControllerInput
          name={`${stopPathName}[${stopIndex}].suite`}
          label={"Suite"}
          className={"flex-1"}
          required={isSuiteRequired}
        />

        {selectedDuration === "hasStopDates" && (
          <div className={"flex-1 ml-2"}>
            <StopDateSelector
              name={`${stopPathName}[${stopIndex}].stopDate`}
              stopType={stopType}
              control={control}
              selectedDuration={selectedDuration}
              onStopDateChange={onStopDateChange}
              index={stopIndex}
            />
          </div>
        )}
      </div>

      {/* Time window row */}
      <div
        className="col-span-1 flex flex-wrap gap-1"
        key={watch(`${stopPathName}[${stopIndex}].timeWindow`)}
      >
        {/* Pickup stop: Ready time */}
        {isPickup && (
          <TimeWindowController
            stopIndex={stopIndex}
            label="Ready"
            name={`${stopPathName}[${stopIndex}].timeWindow.${
              isRecurring ? "openSecondsSinceMidnightUtc" : "open"
            }`}
            control={control}
            formatTime={formatTime}
            watchValue={watchOpenTime}
            className={shouldShowPickupByDate ? "w-24" : "flex-1"}
          />
        )}
        {/* Pickup stop: Pickup By time */}
        {isPickup && (
          <TimeWindowController
            stopIndex={stopIndex}
            label="Pickup By"
            name={`${stopPathName}[${stopIndex}].timeWindow.${
              isRecurring ? "closeSecondsSinceMidnightUtc" : "close"
            }`}
            control={control}
            formatTime={formatTime}
            watchValue={watchCloseTime}
            className={shouldShowPickupByDate ? "w-24" : "flex-1"}
          />
        )}
        {/* Pickup stop: Pickup By date - when showing dates */}
        {shouldShowPickupByDate && (
          <div className="w-28 mt-4">
            <Controller
              control={control}
              name={`${stopPathName}[${stopIndex}].pickupByDate`}
              render={({ field }) => (
                <DatePicker
                  size="mini"
                  value={watch(`${stopPathName}[${stopIndex}].pickupByDate`)}
                  formatString={"MM/dd/yy"}
                  onChange={({ date }) => field.onChange(date)}
                  autoFocusCalendar={false}
                />
              )}
            />
          </div>
        )}
        {/* DropOff stop: Ready time */}
        {isDropoff && (
          <TimeWindowController
            stopIndex={stopIndex}
            label="Ready"
            name={`${stopPathName}[${stopIndex}].timeWindow.${
              isRecurring ? "openSecondsSinceMidnightUtc" : "open"
            }`}
            control={control}
            formatTime={formatTime}
            watchValue={watchOpenTime}
            className={shouldShowDeliverByDate ? "w-24" : "flex-1"}
          />
        )}
        {/* DropOff stop: Deliver By time */}
        {isDropoff && (
          <TimeWindowController
            stopIndex={stopIndex}
            label="Deliver By"
            name={`${stopPathName}[${stopIndex}].timeWindow.${
              isRecurring ? "closeSecondsSinceMidnightUtc" : "close"
            }`}
            control={control}
            formatTime={formatTime}
            watchValue={watchCloseTime}
            className={shouldShowDeliverByDate ? "w-24" : "flex-1"}
          />
        )}
        {/* DropOff/Return stop: Deliver By date - when multi-day */}
        {shouldShowDeliverByDate && (
          <div className="w-28 mt-4">
            <Controller
              control={control}
              name={`${stopPathName}[${stopIndex}].deliverByDate`}
              render={({ field }) => (
                <DatePicker
                  size="mini"
                  value={watch(`${stopPathName}[${stopIndex}].deliverByDate`)}
                  formatString={"MM/dd/yy"}
                  onChange={({ date }) => field.onChange(date)}
                  autoFocusCalendar={false}
                />
              )}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default TimeRowWithTimeWindow;
